// Bildzeichen für Branche (Ebene 1) und Unternehmensfunktion.
//
// WOFÜR: Im Risiken-Reiter steht je Firma eine Zeile Branche und eine Zeile
// Funktion — im Organigramm sind es Kästchen, die nebeneinander liegen. Reiner
// Text zwingt dort zum Lesen jeder einzelnen Zeile; ein Zeichen macht auf einen
// Blick unterscheidbar, welche Firma produziert, welche Grundbesitz hält und
// welche nur verwaltet. Genau diese Unterscheidung trägt die Risikobetrachtung.
//
// WARUM ROHE FRAGMENTE statt fertiger Komponenten: dieselben Zeichen erscheinen
// zweimal in verschiedenen Welten — als HTML-Element in der Firmenliste und
// INNERHALB des Organigramm-SVG. Ein fertiges <svg> mit `width: 100%` ist im
// SVG-Kontext nicht positionierbar. Deshalb liegen hier nur die Pfade im
// 36×36-Raster (wie `riskIcons.tsx`), und jede Seite rahmt sie selbst.

import * as React from "react";

/** Gemeinsames Raster aller Zeichen — identisch zu `riskIcons.tsx`. */
export const ICON_VIEWBOX = "0 0 36 36";

/**
 * Branche (Ebene 1) → Zeichen. Die Schlüssel sind die Ebene-1-Namen des
 * Zwei-Ebenen-Katalogs (`apps/api/src/data/branchengruppen.ts`, 12 Stück).
 * Dazu kommen zwei BESTANDS-Schlüssel („Landwirtschaft" und die alte
 * Sonstige-Branche): Der Risiken-Reiter und das Organigramm füttern
 * `brancheIcon` mit den Ebene-1-Namen des dreistufigen ALTKATALOGS, und
 * Bestandsfirmen sollen ihr Zeichen behalten statt auf das Auffangzeichen zu
 * fallen — dasselbe Muster wie „Verwaltungsgesellschaft" weiter unten.
 * Weicht eine Bezeichnung später ab, greift `brancheIcon` auf das
 * Auffangzeichen zurück — eine fehlende Zuordnung darf die Liste nicht leeren.
 */
export const BRANCHE_ICONS: Record<string, JSX.Element> = {
  "IT & Digitalwirtschaft": (
    <>
      <rect x="12" y="12" width="12" height="12" rx="1.5" />
      <path d="M15 6v6M21 6v6M15 24v6M21 24v6M6 15h6M6 21h6M24 15h6M24 21h6" />
    </>
  ),
  "Industrie & Produktion": (
    <>
      <path d="M5 30V16l7 4v-4l7 4V12l9 6v12z" />
      <line x1="11" y1="26" x2="11" y2="28" />
      <line x1="18" y1="26" x2="18" y2="28" />
      <line x1="25" y1="26" x2="25" y2="28" />
    </>
  ),
  Gesundheitswirtschaft: (
    <>
      <rect x="5" y="10" width="26" height="20" rx="3" />
      <path d="M18 15v10M13 20h10" />
      <path d="M13 10V7a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v3" />
    </>
  ),
  "Energie & Umwelt": (
    <>
      <path d="M20 4l-9 15h6l-1 13 9-15h-6z" />
    </>
  ),
  "Dienstleistung & Freie Berufe": (
    <>
      <rect x="5" y="12" width="26" height="17" rx="2.5" />
      <path d="M14 12V9a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3" />
      <line x1="5" y1="20" x2="31" y2="20" />
    </>
  ),
  "Bau & Handwerk": (
    <>
      <path d="M6 30h24" />
      <path d="M10 30V9l16 5" />
      <path d="M10 9l14 4" />
      <path d="M24 14v8" />
      <rect x="20" y="22" width="8" height="8" rx="1" />
    </>
  ),
  "Transport & Logistik": (
    <>
      <path d="M3 10h15v14H3z" />
      <path d="M18 15h6l5 5v4h-11z" />
      <circle cx="10" cy="27" r="3" />
      <circle cx="25" cy="27" r="3" />
    </>
  ),
  "Öffentliche Hand, Bildung & Forschung": (
    <>
      <path d="M4 14L18 6l14 8" />
      <path d="M7 14v13M13 14v13M23 14v13M29 14v13" />
      <line x1="4" y1="30" x2="32" y2="30" />
    </>
  ),
  // BESTAND: seit 08.09.2026 keine eigene Branche mehr — sie ist in die
  // Sonstige-Branche gewandert. Bestandsfirmen tragen den Wert weiter.
  Landwirtschaft: (
    <>
      <path d="M18 32V12" />
      <path d="M18 16c-5 0-7-3-7-7 5 0 7 3 7 7z" />
      <path d="M18 16c5 0 7-3 7-7-5 0-7 3-7 7z" />
      <path d="M18 26c-5 0-7-3-7-7 5 0 7 3 7 7z" />
      <path d="M18 26c5 0 7-3 7-7-5 0-7 3-7 7z" />
    </>
  ),
  Finanzwirtschaft: (
    <>
      <circle cx="18" cy="18" r="13" />
      <path d="M23 12.5a7 7 0 1 0 0 11" />
      <path d="M10 16h9M10 20h9" />
    </>
  ),
  Immobilienwirtschaft: (
    <>
      <path d="M5 18l13-11 13 11" />
      <path d="M8 16v14h20V16" />
      <rect x="15" y="20" width="6" height="10" />
    </>
  ),
  "Handel & Konsum": (
    <>
      <path d="M4 6h4l3.5 15h14L29 10H9" />
      <circle cx="14" cy="28" r="2.5" />
      <circle cx="25" cy="28" r="2.5" />
    </>
  ),
  // BESTAND: alter Name derselben Branche (bis 08.09.2026).
  "Sonstige (Sport, Kultur, Brauchtum, Gesellschaft etc.)": (
    <>
      <path d="M11 5h14v8a7 7 0 0 1-14 0z" />
      <path d="M11 8H7v2a5 5 0 0 0 4 5M25 8h4v2a5 5 0 0 1-4 5" />
      <path d="M18 20v6M13 31h10l-1-5H14z" />
    </>
  ),
  "Sonstige (Landwirtschaft, Sport, Kultur, Brauchtum, Gesellschaft etc.)": (
    <>
      <path d="M11 5h14v8a7 7 0 0 1-14 0z" />
      <path d="M11 8H7v2a5 5 0 0 0 4 5M25 8h4v2a5 5 0 0 1-4 5" />
      <path d="M18 20v6M13 31h10l-1-5H14z" />
    </>
  ),
};

/** Auffangzeichen für eine Branche ohne eigenes Bild (und für „nicht erfasst"). */
export const BRANCHE_FALLBACK_ICON: JSX.Element = (
  <>
    <rect x="6" y="10" width="24" height="20" rx="2.5" />
    <path d="M6 17h24" />
    <path d="M14 10V7a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3" />
  </>
);

/**
 * Unternehmensfunktion → Zeichen. Bewusst sprechend verschieden: Fabrik für
 * den Betrieb, Haus für den Besitz, Baumstruktur für die Holding.
 *
 * „Verwaltungsgesellschaft" (Aktenmappe) ist seit dem 27.08.2026 nicht mehr
 * anwählbar, steht hier aber weiter: Firmen, die den Wert schon tragen, sollen
 * im Organigramm ihr Zeichen behalten statt auf das Auffangzeichen zu fallen.
 */
export const UNTERNEHMENSFUNKTION_ICONS: Record<string, JSX.Element> = {
  Betriebsgesellschaft: BRANCHE_ICONS["Industrie & Produktion"],
  "Besitz-/Objektgesellschaft": (
    <>
      <path d="M5 17l13-11 13 11" />
      <path d="M8 15v15h20V15" />
      <circle cx="18" cy="21" r="2.5" />
      <path d="M18 23.5V28M18 26h2.5" />
    </>
  ),
  "Holding-/Beteiligungsgesellschaft": (
    <>
      <rect x="13" y="4" width="10" height="7" rx="1.5" />
      <rect x="4" y="25" width="10" height="7" rx="1.5" />
      <rect x="22" y="25" width="10" height="7" rx="1.5" />
      <path d="M18 11v7M9 25v-7h18v7" />
    </>
  ),
  Verwaltungsgesellschaft: (
    <>
      <path d="M4 12h10l3 4h15v14H4z" />
      <path d="M4 12V8h9l3 4" />
      <line x1="12" y1="22" x2="24" y2="22" />
    </>
  ),
};

/** Zeichen einer Ebene-1-Branche; unbekannte Bezeichnung → Auffangzeichen. */
export const brancheIcon = (ebene1: string | null | undefined): JSX.Element =>
  (ebene1 && BRANCHE_ICONS[ebene1.trim()]) || BRANCHE_FALLBACK_ICON;

/** Zeichen einer Unternehmensfunktion; unbekannter Wert → Auffangzeichen. */
export const unternehmensfunktionIcon = (funktion: string | null | undefined): JSX.Element =>
  (funktion && UNTERNEHMENSFUNKTION_ICONS[funktion.trim()]) || BRANCHE_FALLBACK_ICON;

/**
 * Rahmt ein Zeichen als eigenständiges <svg> — für die Verwendung im
 * HTML-Fluss (Firmenliste, Chips). Im Organigramm wird stattdessen direkt
 * `<svg x y width height>` mit denselben Pfaden gesetzt.
 */
export const IconRahmen: React.FC<{
  children: JSX.Element;
  /** Kantenlänge in px; ohne Angabe füllt das Zeichen seinen Platz. */
  groesse?: number;
  titel?: string;
}> = ({ children, groesse, titel }) => (
  <svg
    viewBox={ICON_VIEWBOX}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width={groesse}
    height={groesse}
    style={groesse ? { display: "block" } : { width: "100%", height: "100%", display: "block" }}
    role={titel ? "img" : undefined}
    aria-hidden={titel ? undefined : true}
    aria-label={titel}
  >
    {titel ? <title>{titel}</title> : null}
    {children}
  </svg>
);
